import React, { forwardRef, useCallback, useEffect, useId, useRef } from "react";
import { Form } from "react-bootstrap";

/**
 * Стандартний чекбокс проєкту — той самий підхід, що в «Таблиця PR+» → Налаштування
 * (`HeaderControls`): глобальні стилі з `style.scss` / `_variables.scss`
 * (прихований нативний input + квадратик і галочка на `label`).
 *
 * Явно зв’язуємо `label` і `input` через `htmlFor` / `id` (у т.ч. у модалках Bootstrap).
 * Рекомендовано передавати стабільний унікальний `id`.
 *
 * `indeterminate` — лише через DOM (`input.indeterminate`), для master checkbox у таблицях.
 */
export const AppCheckbox = forwardRef(function AppCheckbox(
    { id: idProp, label, checked, onChange, disabled, className, indeterminate, style, title, ...rest },
    ref
) {
    const autoId = useId();
    const inputId = idProp != null && String(idProp).length > 0 ? idProp : autoId;
    const innerRef = useRef(null);

    const setInputRef = useCallback(
        (node) => {
            innerRef.current = node;
            if (typeof ref === "function") {
                ref(node);
            } else if (ref != null) {
                ref.current = node;
            }
        },
        [ref]
    );

    useEffect(() => {
        const el = innerRef.current;
        if (el) {
            el.indeterminate = Boolean(indeterminate);
        }
    }, [indeterminate]);

    return (
        <div
            className={className ? `form-check ${className}`.trim() : "form-check"}
            style={style}
            title={title}
        >
            <input
                {...rest}
                ref={setInputRef}
                type="checkbox"
                id={inputId}
                className="form-check-input"
                checked={checked}
                onChange={onChange}
                disabled={disabled}
            />
            <label className="form-check-label" htmlFor={inputId}>
                {label}
            </label>
        </div>
    );
});

AppCheckbox.displayName = "AppCheckbox";

/**
 * Група чекбоксів — обгортка `checkbox-holder`, як для джерел/статусів у `HeaderControls`.
 */
export function CheckboxHolder({ children, className, formClassName }) {
    return (
        <div className={className ? `checkbox-holder ${className}`.trim() : "checkbox-holder"}>
            <Form className={formClassName}>{children}</Form>
        </div>
    );
}
